/**
 * Hook for managing offline/online state and sync queue replay.
 *
 * When the app goes offline, document saves are queued in IndexedDB.
 * When connectivity returns, queued edits are replayed in order.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { offlineStorage } from "../lib/offlineStorage";
import { api } from "../lib/api";

interface UseOfflineSyncReturn {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  replayQueue: () => Promise<void>;
  saveOffline: (documentId: string, content: string) => Promise<void>;
}

export function useOfflineSync(): UseOfflineSyncReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const replayingRef = useRef(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      replayQueue();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    refreshPendingCount();

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const refreshPendingCount = async () => {
    try {
      const queue = await offlineStorage.getSyncQueue();
      setPendingCount(queue.length);
    } catch {
      // IndexedDB may not be available
    }
  };

  const replayQueue = useCallback(async () => {
    if (replayingRef.current || !navigator.onLine) return;
    replayingRef.current = true;
    setIsSyncing(true);

    try {
      const queue = await offlineStorage.getSyncQueue();
      for (const item of queue) {
        try {
          await api.put(`/api/v1/documents/${item.documentId}`, {
            content: item.content,
          });
          await offlineStorage.removeSyncItem(item.id);
        } catch {
          break;
        }
      }
    } catch {
      // Non-critical
    } finally {
      replayingRef.current = false;
      setIsSyncing(false);
      await refreshPendingCount();
    }
  }, []);

  const saveOffline = useCallback(async (documentId: string, content: string) => {
    await offlineStorage.enqueueSync({
      documentId,
      content,
      timestamp: Date.now(),
    });
    await refreshPendingCount();
  }, []);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    replayQueue,
    saveOffline,
  };
}
